import { X } from 'lucide-react';
import * as React from 'react';

import { GridSensitivityChip } from '../../atoms/GridSensitivityChip';
import { TagChip } from '../../atoms/TagChip';
import { resolveActionIcon, resolveUserTypeBadge } from '../../lib/access-legitimacy-resolvers';
import { humanizeLabel } from '../../lib/humanize-label';
import { cn } from '../../lib/utils';
import {
  EventActionHistoryTable,
  type EventActionHistoryRecord,
} from '../../molecules/EventActionHistoryTable';
import { EventIdentityRow } from '../../molecules/EventIdentityRow';
import { FeedbackPanel, type FeedbackPanelThumb } from '../../molecules/FeedbackPanel';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '../../molecules/Sheet';

import type { AccessLegitimacyDetailDrawerLabels, AccessLegitimacyEventDetail } from './types';

/** Maps a wire action-history entry to the table's render record. */
function toActionRecord(
  entry: AccessLegitimacyEventDetail['actionHistory'][number],
  eventId: string,
  index: number
): EventActionHistoryRecord {
  const badge = resolveUserTypeBadge(entry.userType);
  return {
    id: `${eventId}-action-${index}`,
    dateTime: entry.dateTime,
    userType: badge.type === 'NHI' ? 'serviceAccount' : 'human',
    isExternal: badge.isExternal,
    actor: entry.actor,
    action: humanizeLabel(entry.action),
    actionIconId: resolveActionIcon(entry.action),
  };
}

/**
 * Resolved label shape — every field is required after merging `DEFAULT_LABELS`
 * with caller overrides. `actionHistoryColumns` is deep-required so column
 * cells can be looked up without optional-chaining noise inside the table.
 */
interface ResolvedLabels extends Required<
  Omit<AccessLegitimacyDetailDrawerLabels, 'actionHistoryColumns'>
> {
  actionHistoryColumns: Required<
    NonNullable<AccessLegitimacyDetailDrawerLabels['actionHistoryColumns']>
  >;
}

const DEFAULT_LABELS: ResolvedLabels = {
  closeAriaLabel: 'Close event details',
  takeAction: 'Take Action',
  illegitimacyReason: 'Illegitimacy Reason',
  summary: 'Summary',
  ddcClassification: 'DDC Classification',
  additionalTopics: 'Additional Topics',
  scopes: 'Scopes',
  sensitivity: 'Sensitivity',
  namesInFile: 'Names appearing in the file',
  actionHistory: 'Action taken on the file',
  actionHistoryColumns: {
    dateTime: 'Date & time',
    userType: 'User type',
    actor: 'Actor',
    action: 'Action',
  },
  feedbackPrompt: 'What do you think about this analysis?',
  feedbackPlaceholder: 'Explain why...',
  feedbackSubmittedText: 'Thanks for your feedback',
};

interface FeedbackState {
  thumb: FeedbackPanelThumb;
  submitted: boolean;
  comment: string;
}

const INITIAL_FEEDBACK: FeedbackState = {
  thumb: null,
  submitted: false,
  comment: '',
};

export interface AccessLegitimacyDetailDrawerProps {
  /** Detail record. When undefined while `open` is true, the skeleton renders. */
  event: AccessLegitimacyEventDetail | undefined;
  /** Controlled visibility (owned by the parent body). */
  open: boolean;
  /** Fires on Escape, overlay click, × glyph click. */
  onClose: () => void;
  /** Stub click handler for the "Take Action" CTA (V2: visual-only). The CTA and
   *  its skeleton slot render only when this is set (dormant for the POV, TDEV-1130). */
  onTakeAction?: () => void;
  /** Stub click handlers for the feedback thumbs (V2: visual-only). */
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
  /** Stub callback for feedback comment submission (V2: visual-only). */
  onFeedbackSubmit?: (comment: string) => void;
  /** Formatter for identity-row timestamp + action-history dates (ISO strings). */
  formatDateTime: (iso: string) => string;
  /** Caller-owned i18n label overrides. */
  labels?: AccessLegitimacyDetailDrawerLabels;
  /** Force the skeleton even when `event` is defined. */
  loading?: boolean;
  /** Extra classes merged onto the `SheetContent` element. */
  className?: string;
}

/**
 * `AccessLegitimacyDetailDrawer` — right-anchored drawer organism that renders
 * a single Access-Legitimacy event's detail surface. Composes the canonical
 * shadcn `Sheet` primitive (P2) for chrome (overlay, focus trap, Escape,
 * focus-restore) and three Access-Legit content molecules inside:
 * `EventIdentityRow`, `EventActionHistoryTable`, and `FeedbackPanel`.
 *
 * Behaviour contract (design §3.1 + §7):
 * - `open=false` → unmounted (Radix portal absent).
 * - `open=true` + `event=undefined` → skeleton (data still loading).
 * - `open=true` + `loading=true` → skeleton even if event is defined.
 * - `open=true` + event + `!loading` → 10 sections per PRD §10, with section
 *   omission for empty optional fields per PRD §12.
 * - Drawer-internal feedback state (thumb, comment, submitted) resets when
 *   `event.id` changes — the body keeps the drawer mounted while switching
 *   between events, so this organism owns the reset semantics.
 */
export function AccessLegitimacyDetailDrawer({
  event,
  open,
  onClose,
  onTakeAction,
  onThumbsUp,
  onThumbsDown,
  onFeedbackSubmit,
  formatDateTime,
  labels,
  loading,
  className,
}: AccessLegitimacyDetailDrawerProps): React.ReactElement {
  const L: ResolvedLabels = {
    ...DEFAULT_LABELS,
    ...labels,
    actionHistoryColumns: {
      ...DEFAULT_LABELS.actionHistoryColumns,
      ...(labels?.actionHistoryColumns ?? {}),
    },
  };

  const [feedback, setFeedback] = React.useState<FeedbackState>(INITIAL_FEEDBACK);

  // Reset feedback when (a) the drawer swaps to a new event without
  // unmounting (parent toggles `event` while keeping `open` true) OR
  // (b) the drawer reopens on the same event id — without the `open`
  // guard, the `lastEventRef` pattern keeps `event?.id` stable through
  // the close animation, so a re-open on the same row would otherwise
  // surface the stale `submitted = true` state from the prior session.
  React.useEffect(() => {
    if (open) setFeedback(INITIAL_FEEDBACK);
  }, [event?.id, open]);

  const isLoading = loading === true || (open && event === undefined);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose]
  );

  const handleThumb = React.useCallback(
    (thumb: 'up' | 'down') => {
      // Skip external callbacks when the click is a deselection (toggle-off)
      // so downstream observers (analytics, backend) can distinguish a fresh
      // selection from a click that clears the prior choice. The updater fn
      // stays pure (assignment to outer var only) so React 18 Strict Mode's
      // double-invocation is idempotent.
      let willDeselect = false;
      setFeedback((prev) => {
        willDeselect = prev.thumb === thumb;
        return { ...prev, thumb: willDeselect ? null : thumb };
      });
      if (willDeselect) return;
      if (thumb === 'up') {
        onThumbsUp?.();
      } else {
        onThumbsDown?.();
      }
    },
    [onThumbsUp, onThumbsDown]
  );

  const handleCommentChange = React.useCallback((value: string) => {
    setFeedback((prev) => ({ ...prev, comment: value }));
  }, []);

  const handleCommentBlur = React.useCallback(() => {
    // Keep the updater fn pure — React 18 Strict Mode invokes setState
    // updaters twice to surface impurity, which would otherwise fire
    // `onFeedbackSubmit` twice per blur in any StrictMode-wrapped tree.
    // Capture the comment in a local, then invoke the callback after
    // the updater returns.
    let commentToSubmit: string | null = null;
    setFeedback((prev) => {
      const trimmed = prev.comment.trim();
      if (trimmed.length === 0 || prev.submitted) {
        return prev;
      }
      // Submit the trimmed value — what we validated (`trimmed.length > 0`)
      // must match what downstream observers receive, so a comment like
      // "  useful  " ships as "useful" rather than with leading/trailing
      // whitespace bleed.
      commentToSubmit = trimmed;
      return { ...prev, submitted: true };
    });
    if (commentToSubmit !== null) {
      onFeedbackSubmit?.(commentToSubmit);
    }
  }, [onFeedbackSubmit]);

  const srTitle = event
    ? `Event detail: ${event.actor}${event.documentSubject ? ` — ${event.documentSubject}` : ''}`
    : 'Event detail loading';

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        hideCloseButton
        aria-modal="true"
        aria-describedby={undefined}
        className={cn('flex w-[640px] flex-col gap-4 overflow-y-auto p-5 sm:max-w-none', className)}
      >
        <SheetTitle className="sr-only">{srTitle}</SheetTitle>
        <SheetClose
          aria-label={L.closeAriaLabel}
          className="inline-flex h-3 w-3 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-dawn-orange focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
        </SheetClose>
        {isLoading ? (
          <DrawerSkeleton hasTakeAction={onTakeAction !== undefined} />
        ) : (
          <DrawerBody
            event={event as AccessLegitimacyEventDetail}
            labels={L}
            feedback={feedback}
            formatDateTime={formatDateTime}
            onTakeAction={onTakeAction}
            onThumbClick={handleThumb}
            onCommentChange={handleCommentChange}
            onCommentBlur={handleCommentBlur}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

AccessLegitimacyDetailDrawer.displayName = 'AccessLegitimacyDetailDrawer';

// =============================================================================
// Skeleton — grey-bar placeholders mirroring the section structure.
// =============================================================================

function DrawerSkeleton({ hasTakeAction }: { hasTakeAction: boolean }): React.ReactElement {
  return (
    <div
      data-testid="access-legitimacy-detail-drawer-skeleton"
      className="flex flex-col gap-5"
      aria-busy="true"
    >
      {/* Header row stubs. */}
      <div className="flex flex-wrap items-center gap-1">
        <div className="h-5 w-32 animate-pulse rounded-[2px] bg-grey-100" />
        <div className="h-5 w-20 animate-pulse rounded-[2px] bg-grey-100" />
        <div className="h-5 w-24 animate-pulse rounded-[2px] bg-grey-100" />
        {hasTakeAction && (
          <div
            data-testid="access-legitimacy-detail-drawer-skeleton-take-action"
            className="ml-auto h-7 w-28 animate-pulse rounded-[4px] bg-grey-100"
          />
        )}
      </div>
      <hr className="border-t border-grey-200" />
      {/* Paragraph stubs — 4 sections. */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} className="flex flex-col gap-3">
          <div className="h-4 w-28 animate-pulse rounded-[2px] bg-grey-100" />
          <div className="h-4 w-full animate-pulse rounded-[2px] bg-grey-100" />
          <div className="h-4 w-3/4 animate-pulse rounded-[2px] bg-grey-100" />
        </div>
      ))}
      {/* Mini-table stubs. */}
      <div className="flex flex-col gap-1">
        <div className="h-4 w-32 animate-pulse rounded-[2px] bg-grey-100" />
        <div className="h-7 w-full animate-pulse rounded-[2px] bg-grey-100" />
        <div className="h-7 w-full animate-pulse rounded-[2px] bg-grey-100" />
        <div className="h-7 w-full animate-pulse rounded-[2px] bg-grey-100" />
      </div>
    </div>
  );
}

// =============================================================================
// Body — 10 sections per PRD §10.
// =============================================================================

interface DrawerBodyProps {
  event: AccessLegitimacyEventDetail;
  labels: ResolvedLabels;
  feedback: FeedbackState;
  formatDateTime: (iso: string) => string;
  onTakeAction?: () => void;
  onThumbClick: (thumb: 'up' | 'down') => void;
  onCommentChange: (value: string) => void;
  onCommentBlur: () => void;
}

function DrawerBody({
  event,
  labels,
  feedback,
  formatDateTime,
  onTakeAction,
  onThumbClick,
  onCommentChange,
  onCommentBlur,
}: DrawerBodyProps): React.ReactElement {
  const additionalTopics = event.topics.slice(1);
  const actionRecords = event.actionHistory.map((entry, i) => toActionRecord(entry, event.id, i));

  return (
    <div className="flex flex-col gap-5">
      <EventIdentityRow
        actor={event.actor}
        fileName={event.fileName}
        fileUrl={event.fileUrl}
        userType={event.userType}
        dateTime={event.dateTime}
        documentType={event.documentType}
        documentSubject={event.documentSubject}
        topic={event.topics[0] ?? null}
        fileType={event.fileType}
        sensitivity={event.sensitivity}
        location={event.location}
        formatDateTime={formatDateTime}
        onTakeAction={onTakeAction}
        takeActionLabel={labels.takeAction}
      />

      <hr className="border-t border-grey-200" />

      {event.illegitimacyReason ? (
        <Section label={labels.illegitimacyReason}>
          <p className="font-outfit text-[13px] font-normal leading-[1.3] text-dawn-black">
            {event.illegitimacyReason}
          </p>
        </Section>
      ) : null}

      <Section label={labels.summary}>
        <p className="font-outfit text-[13px] font-normal leading-[1.3] text-dawn-black">
          {event.summary ?? '—'}
        </p>
      </Section>

      {event.ddcClassification.length > 0 ? (
        <Section label={labels.ddcClassification}>
          <div className="flex flex-wrap gap-1">
            {event.ddcClassification.map((ddc) => (
              <TagChip key={ddc} state="subtle" size="sm">
                {humanizeLabel(ddc)}
              </TagChip>
            ))}
          </div>
        </Section>
      ) : null}

      {additionalTopics.length > 0 ? (
        <Section label={labels.additionalTopics}>
          <div className="flex flex-wrap gap-1">
            {additionalTopics.map((topic) => (
              <TagChip key={topic} state="subtle" size="sm">
                {humanizeLabel(topic)}
              </TagChip>
            ))}
          </div>
        </Section>
      ) : null}

      {event.scopes.length > 0 ? (
        <Section label={labels.scopes}>
          <div className="flex flex-wrap gap-1">
            {event.scopes.map((scope) => (
              <TagChip key={scope} state="subtle" size="sm">
                {humanizeLabel(scope)}
              </TagChip>
            ))}
          </div>
        </Section>
      ) : null}

      <Section label={labels.sensitivity}>
        <div className="flex flex-wrap items-center gap-1">
          <GridSensitivityChip sensitivity={event.sensitivity} />
        </div>
      </Section>

      {event.namesInFile.length > 0 ? (
        <Section label={labels.namesInFile}>
          <div className="flex flex-wrap gap-1">
            {event.namesInFile.map((name) => (
              <TagChip key={name} state="subtle" size="sm">
                {name}
              </TagChip>
            ))}
          </div>
        </Section>
      ) : null}

      {actionRecords.length > 0 ? (
        <Section label={labels.actionHistory}>
          <EventActionHistoryTable
            records={actionRecords}
            columns={labels.actionHistoryColumns}
            formatDateTime={formatDateTime}
          />
        </Section>
      ) : null}

      <Section label={labels.feedbackPrompt}>
        <FeedbackPanel
          thumb={feedback.thumb}
          comment={feedback.comment}
          submitted={feedback.submitted}
          onThumbClick={onThumbClick}
          onCommentChange={onCommentChange}
          onCommentBlur={onCommentBlur}
          labels={{
            prompt: labels.feedbackPrompt,
            placeholder: labels.feedbackPlaceholder,
            submittedText: labels.feedbackSubmittedText,
          }}
        />
      </Section>
    </div>
  );
}

// =============================================================================
// Section helper — renders the label + child content. Same layout used across
// every section so spacing rhythms match Figma `5:10231`.
// =============================================================================

interface SectionProps {
  label: string;
  children: React.ReactNode;
}

function Section({ label, children }: SectionProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-3">
      <p className="font-outfit text-[13px] font-medium leading-[1.3] text-dawn-black">{label}</p>
      {children}
    </div>
  );
}
